import { Fields, FormGroups } from 'common/form';
import {
  CalendarMeetingTemplateFormData,
  MeetingLocation,
  MeetingUnit
} from './calendarMeetingTemplateSchema';
import { AvailabilityMethod } from 'generated-graphql/graphql';
import { formatTimeZoneLabel } from 'modules/calendar/utils/formatTimeZoneLabel';
import { UseUserCalendarReturnData } from 'modules/calendar/hooks/useUserCalendar';
import { Stack, SxProps, Typography } from '@mui/material';
import { MeetingPanel } from 'modules/calendar/components/MeetingPanel';
import { isPanelTypeSelectorVisible } from 'modules/calendar/utils/isPanelTypeSelectorVisible';
import { RouterLink } from 'common/ui/RouterLink';
import { Paths } from 'services/navigation/paths';
import { DEFAULT_REQUIRED_HOURS } from './requiredHoursDefault';

const valueSx: SxProps = {
  width: '100px'
};
const unitSx: SxProps = {
  width: '150px'
};

const SchedulingMethodOption = ({
  title,
  description
}: {
  title: string;
  description: string;
}) => (
  <Stack>
    <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
    <Typography variant="body2" sx={{ color: 'customGrey.dark' }}>
      {description}
    </Typography>
  </Stack>
);

const schedulingMethodOptions = [
  {
    value: AvailabilityMethod.Collective,
    label: (
      <SchedulingMethodOption
        title="Whole panel"
        description="Candidates can only book when every panellist is free."
      />
    )
  },
  {
    value: AvailabilityMethod.RoundRobinMaxAvailability,
    label: (
      <SchedulingMethodOption
        title="Single panel member"
        description="Maximise booking slots available to the candidate. Some panellists may receive significantly more bookings than others."
      />
    )
  }
];

const roundRobinMethodOptions = schedulingMethodOptions.filter(
  (option) => option.value !== AvailabilityMethod.Collective
);

const getMeetingUnitOptions = ({ withoutDays }: { withoutDays?: boolean }) => {
  return Object.values(MeetingUnit)
    .filter((unit) => !withoutDays || unit !== MeetingUnit.Days)
    .map((unit) => ({
      label: unit,
      value: unit
    }));
};

export const getCalendarMeetingTemplateFields = (
  type: 'create' | 'edit' | 'preview',
  userCalendars: UseUserCalendarReturnData,
  {
    ownerId,
    isLocationInPersonSelected,
    participantCount,
    hasVirtualParticipant,
    isRoundRobinSelected,
    unresolvedMembers,
    onOrganizerChange
  }: {
    ownerId: string | null;
    isLocationInPersonSelected: boolean;
    participantCount: number;
    hasVirtualParticipant: boolean;
    isRoundRobinSelected: boolean;
    unresolvedMembers?: { email: string; name: string }[];
    onOrganizerChange: (id: string) => void;
  }
): Fields<CalendarMeetingTemplateFormData> => {
  const getDefaultValue = (value: any) =>
    type === 'create' ? value : undefined;

  const getDefaultAvailability = () =>
    type === 'create'
      ? [[new Date('2022-01-01T09:00:00'), new Date('2022-01-01T17:00:00')]]
      : undefined;

  const isPanelTypeVisible = isPanelTypeSelectorVisible({
    type,
    participantCount,
    isRoundRobinSelected
  });

  return {
    title: {
      label: 'Template title',
      subLabel: 'E.g. 15 minute phone call.',
      type: 'text'
    },
    description: {
      label: 'Description (optional)',
      subLabel:
        'Sent to the candidate with the invitation. You can include a link.',
      type: 'textarea'
    },
    participants: {
      label: 'Choose interviewers',
      // New tab: the form has no unsaved-changes guard, so navigating in place
      // loses a half-filled template.
      subLabel: (
        <RouterLink to={Paths.HOME_CALENDAR_MEETINGS} target="_blank">
          Invite or edit meeting panellists
        </RouterLink>
      ),
      type: 'custom',
      defaultValue: [],
      render: (props) => (
        <MeetingPanel
          {...props}
          userCalendars={userCalendars}
          ownerId={ownerId}
          isRoundRobin={isRoundRobinSelected}
          onOrganizerChange={onOrganizerChange}
          lockOrganizer={type !== 'create'}
          unresolvedMembers={unresolvedMembers}
          // Only on create can a second interviewer reveal the control.
          showPanelTypeHint={!isPanelTypeVisible && type === 'create'}
        />
      )
    },
    // Set by the panel's organizer marker; hidden so it still validates.
    owner: {
      type: 'text',
      hidden: true
    },
    schedulingMethod: {
      label: 'Interview panel',
      subLabel:
        type === 'create'
          ? 'Whole panel or Single panel member is fixed once the template is created.'
          : undefined,
      type: 'radio',
      // Crossing the collective boundary orphans the Nylas configuration.
      options:
        type === 'create' ? schedulingMethodOptions : roundRobinMethodOptions,
      defaultValue: getDefaultValue(AvailabilityMethod.Collective),
      hidden: !isPanelTypeVisible
    },
    location: {
      type: 'radio',
      options: Object.values(MeetingLocation).map((location) => ({
        label: location,
        value: location
      })),
      defaultValue: getDefaultValue(MeetingLocation.Video),
      group: 'location'
    },
    locationDetails: {
      label: 'Location details',
      subLabel: 'Include an address in the invitation.',
      type: 'text',
      group: 'location',
      hidden: !isLocationInPersonSelected
    },
    durationValue: {
      type: 'text',
      containerSx: valueSx,
      group: 'duration',
      defaultValue: getDefaultValue(30)
    },
    durationUnit: {
      type: 'select',
      options: getMeetingUnitOptions({
        withoutDays: true
      }),
      defaultValue: MeetingUnit.Minutes,
      containerSx: unitSx,
      group: 'duration'
    },
    mondayAvailability: {
      group: 'availability',
      checkboxLabel: 'Monday',
      type: 'timeSlots',
      defaultValue: getDefaultAvailability()
    },
    tuesdayAvailability: {
      group: 'availability',
      checkboxLabel: 'Tuesday',
      type: 'timeSlots',
      defaultValue: getDefaultAvailability()
    },
    wednesdayAvailability: {
      group: 'availability',
      checkboxLabel: 'Wednesday',
      type: 'timeSlots',
      defaultValue: getDefaultAvailability()
    },
    thursdayAvailability: {
      group: 'availability',
      checkboxLabel: 'Thursday',
      type: 'timeSlots',
      defaultValue: getDefaultAvailability()
    },
    fridayAvailability: {
      group: 'availability',
      checkboxLabel: 'Friday',
      type: 'timeSlots',
      defaultValue: getDefaultAvailability()
    },
    saturdayAvailability: {
      group: 'availability',
      checkboxLabel: 'Saturday',
      type: 'timeSlots'
    },
    sundayAvailability: {
      group: 'availability',
      checkboxLabel: 'Sunday',
      type: 'timeSlots'
    },
    timeZone: {
      label: 'Timezone',
      type: 'dropdown',
      options: Intl.supportedValuesOf('timeZone').map((timeZone) => ({
        label: formatTimeZoneLabel(timeZone),
        value: timeZone
      })),
      isSearchable: true,
      searchPlaceholder: 'Search for a city',
      defaultValue: getDefaultValue(
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )
    },
    sendMeetingReminderValue: {
      type: 'text',
      containerSx: valueSx,
      group: 'sendMeetingReminder',
      defaultValue: getDefaultValue(15)
    },
    sendMeetingReminderUnit: {
      type: 'select',
      options: getMeetingUnitOptions({}),
      defaultValue: MeetingUnit.Minutes,
      containerSx: unitSx,
      group: 'sendMeetingReminder'
    },
    bufferTimeValue: {
      type: 'text',
      containerSx: valueSx,
      group: 'bufferTime',
      defaultValue: getDefaultValue(30)
    },
    bufferTimeUnit: {
      type: 'select',
      options: getMeetingUnitOptions({
        withoutDays: true
      }),
      defaultValue: MeetingUnit.Minutes,
      containerSx: unitSx,
      group: 'bufferTime'
    },
    noticePeriodValue: {
      type: 'text',
      containerSx: valueSx,
      group: 'noticePeriod',
      defaultValue: getDefaultValue(1)
    },
    noticePeriodUnit: {
      type: 'select',
      options: getMeetingUnitOptions({}),
      defaultValue: MeetingUnit.Days,
      containerSx: unitSx,
      group: 'noticePeriod'
    },
    bookBeforeValue: {
      label: 'How far ahead candidates can book (days)',
      type: 'text',
      sx: {
        ...valueSx,
        mt: 1
      },
      defaultValue: getDefaultValue(14)
    },
    requiredHours: {
      label: 'Minimum availability to request (hours)',
      subLabel:
        'How much time panellists without a connected calendar are asked to offer.',
      type: 'text',
      sx: {
        ...valueSx,
        mt: 1
      },
      defaultValue: getDefaultValue(DEFAULT_REQUIRED_HOURS),
      hidden: !hasVirtualParticipant
    }
  };
};

export const getCalendarMeetingTemplateGroups = ({
  isRoundRobinSelected
}: {
  isRoundRobinSelected: boolean;
}): FormGroups => ({
  duration: {
    label: 'Duration'
  },
  availability: {
    label: 'Availability',
    subLabel: isRoundRobinSelected
      ? 'Slots are offered when any panellist is free.'
      : 'Slots are offered only when every panellist is free.'
  },
  sendMeetingReminder: {
    label: 'Send reminder before the meeting',
    subLabel: 'Sent to the candidate and everyone on the panel.'
  },
  bufferTime: {
    label: 'Buffer time',
    subLabel: 'Keep time free between meetings.'
  },
  noticePeriod: {
    label: 'Notice period',
    subLabel: 'Stop candidates booking at short notice.'
  },
  location: {
    label: 'Location'
  }
});
